import { useEffect, useState } from 'react';
import { ArrowRight, Clock3, Coffee, HeartHandshake, MapPin, MessageCircle, Phone, ShieldCheck, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import ServiceGrid from '../components/ServiceGrid';
import SeasonalDecorations from '../components/SeasonalDecorations';
import SeasonalAudio from '../components/SeasonalAudio';
import TextNowLink from '../components/TextNowLink';
import { CONTACT, REVIEWS } from '../data/siteData';
import { resolveTheme } from '../seasonal/seasonalEngine';
import { loadPublicThemeOverride } from '../lib/themeSettings';
import { ORGANIZATION_ID, SITE_URL, WEBSITE_ID } from '../lib/seoData';
import { useAuth } from '../lib/AuthContext';

export default function Home() {
  const { isAdmin } = useAuth();
  const [override, setOverride] = useState('automatic');
  const [resolved, setResolved] = useState(() => resolveTheme({ override: 'automatic' }));

  useEffect(() => {
    let active = true;
    loadPublicThemeOverride().then((value) => { if (active) setOverride(value); });
    const refresh = () => loadPublicThemeOverride().then((value) => { if (active) setOverride(value); });
    window.addEventListener('jonnys-theme-change', refresh);
    return () => { active = false; window.removeEventListener('jonnys-theme-change', refresh); };
  }, []);

  useEffect(() => {
    setResolved(resolveTheme({ override, allowPreview: isAdmin || window.location.hostname === 'localhost' }));
  }, [override, isAdmin]);

  const { theme } = resolved;
  const homeSeoTitle = 'Towing & Roadside Assistance Richmond BC';
  const homeSeoDescription = "Richmond-based towing, flatbed transport, recovery and roadside assistance across Greater Vancouver. Call Jonny's Towing at (604) 398-8866.";
  const homePageSchema = {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#home`,
    url: SITE_URL,
    name: homeSeoTitle,
    description: homeSeoDescription,
    about: { '@id': ORGANIZATION_ID },
    isPartOf: { '@id': WEBSITE_ID },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: `${SITE_URL}${theme.image}`,
    },
    inLanguage: 'en-CA',
  };

  return (
    <>
      <Seo title={homeSeoTitle} description={homeSeoDescription} image={theme.image} canonicalPath="/" structuredData={homePageSchema} />
      <SeasonalAudio music={theme.music} themeLabel={theme.label} />
      <section className={`seasonal-hero ${theme.className}`}>
        <div className="seasonal-hero__media" style={{ backgroundImage: `linear-gradient(90deg, rgba(3,4,7,.97) 0%, rgba(3,4,7,.78) 47%, rgba(3,4,7,.18) 100%), url(${theme.image})` }} />
        <SeasonalDecorations types={theme.decorations} />
        <div className="seasonal-hero__content container">
          {theme.id !== 'halloween' && <span className="eyebrow">{theme.eyebrow}</span>}
          <p className="service-identity">Towing &amp; Roadside Assistance in Richmond, BC</p>
          <h1>Tow Truck &amp; Roadside Assistance in Richmond, BC</h1>
          <p className="seasonal-hero__service-copy">Local towing, flatbed transport, winching and vehicle recovery, jump starts, lockout assistance and spare tire changes. Call or text to confirm service availability and an appropriate truck.</p>
          <div className="hero-actions">
            <a className="button button--primary" href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch"><Phone size={19} /> Call {CONTACT.phoneDisplay}</a>
            <TextNowLink className="button button--ghost"><MessageCircle size={19} /> Text Now</TextNowLink>
            <Link className="button button--ghost" to="/contact#service-request" data-roka-cta="request_tow_home" data-roka-label="Request towing">Request a tow <ArrowRight size={18} /></Link>
          </div>
          <div className="hero-proof">
            <span><Star size={18} fill="currentColor" /> 700+ five-star reviews</span>
            <span><MapPin size={18} /> Richmond based</span>
            <span><Clock3 size={18} /> Local roadside response</span>
          </div>
        </div>
      </section>

      <section className="trust-band">
        <div className="container trust-band__grid">
          <div><ShieldCheck /><span><strong>Professional handling</strong>Careful service for your vehicle</span></div>
          <div><HeartHandshake /><span><strong>Real human support</strong>Calm, respectful communication</span></div>
          <div><MapPin /><span><strong>Local knowledge</strong>Richmond and Greater Vancouver</span></div>
        </div>
      </section>

      {theme.seasonalMessage && (
        <section className={`seasonal-message ${theme.className}`}>
          <div className="container seasonal-message__inner">
            <span className="seasonal-message__icon" aria-hidden="true">{theme.seasonalMessage.icon}</span>
            <div>
              <span className="eyebrow">A seasonal note from Jonny’s</span>
              <h2>{theme.seasonalMessage.title}</h2>
              <p className="seasonal-message__headline">{theme.title}</p>
              <p>{theme.description}</p>
              <p>{theme.seasonalMessage.text}</p>
            </div>
          </div>
        </section>
      )}

      <section className="section container">
        <div className="section-heading split-heading">
          <div>
            <span className="eyebrow">Roadside services</span>
            <h2>Whatever stopped the journey, we help get it moving again.</h2>
          </div>
          <Link className="text-link" to="/services">View every service <ArrowRight size={18} /></Link>
        </div>
        <ServiceGrid limit={6} />
      </section>

      <section className="section section--dark">
        <div className="container feature-split">
          <div className="feature-image-wrap">
            <img src="/images/flatbed-sunset.webp" alt="Jonny's Towing flatbed at sunset" />
            <div className="feature-stat"><strong>More than a tow</strong><span>Relief, confidence and care</span></div>
          </div>
          <div className="feature-copy">
            <span className="eyebrow">The Jonny’s experience</span>
            <h2>We are not just moving vehicles. We are helping people through difficult moments.</h2>
            <p>Breakdowns, collisions and dead batteries are rarely convenient. Our job is to bring calm communication, capable equipment and thoughtful service to a situation that already feels heavy.</p>
            <ul className="check-list">
              <li>Real local people who know the area</li>
              <li>Clear communication from first call to completion</li>
              <li>Unexpected touches that make a bad day better</li>
              <li>Community-minded service with personality</li>
            </ul>
            <Link className="button button--secondary" to="/experiences">Explore the experience <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <section className="section nespresso-home">
        <div className="container nespresso-home__panel">
          <div className="nespresso-home__media">
            <img src="/images/rolls-royce-flatbed.webp" alt="Premium vehicle transport by Jonny's Towing" />
            <div className="nespresso-home__badge"><Coffee size={22} /> A Jonny’s signature experience</div>
          </div>
          <div className="nespresso-home__copy">
            <span className="eyebrow">Jonny’s Towing × Nespresso</span>
            <h2>A difficult morning deserves one thoughtful detail.</h2>
            <p>On eligible early-morning flatbed calls, we may offer customers a freshly brewed Nespresso coffee at no charge. It is a small premium touch, but it represents exactly how we approach roadside service: capable, personal and unexpectedly considerate.</p>
            <ul className="check-list">
              <li>Freshly brewed on eligible early-morning calls</li>
              <li>A rotating selection of Nespresso blends</li>
              <li>Complimentary when available</li>
            </ul>
            <Link className="button button--secondary" to="/jonnys-towing-x-nespresso">Discover the Nespresso experience <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <section className="section container">
        <div className="section-heading centered-heading">
          <span className="eyebrow">Why Richmond calls Jonny’s</span>
          <h2>Reputation built one difficult day at a time.</h2>
          <p>Every review represents someone who trusted us with their vehicle, their schedule and their peace of mind.</p>
        </div>
        <div className="review-grid">
          {REVIEWS.map((review) => (
            <article className="review-card" key={review.quote}>
              <div className="stars">★★★★★</div>
              <blockquote>“{review.quote}”</blockquote>
              <span>{review.name}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="section community-spotlight">
        <div className="container feature-split feature-split--reverse">
          <div className="feature-copy">
            <span className="eyebrow">More than roadside service</span>
            <h2>Black trucks. Bright-pink accents. Community events people remember.</h2>
            <p>From school pumpkin patches and holiday neighbourhood visits to elaborate Halloween experiences, Jonny’s Towing uses creativity and community reach to bring people together.</p>
            <div className="hero-actions">
              <Link className="button button--primary" to="/in-the-community">See community projects</Link>
              <Link className="button button--ghost" to="/halloween-holiday-trucks">Halloween &amp; holiday trucks</Link>
            </div>
          </div>
          <div className="feature-image-wrap feature-image-wrap--collage">
            <img src="/images/halloween-truck.webp" alt="Halloween decorated Jonny's Towing truck" />
            <img src="/images/christmas-truck.webp" alt="Christmas decorated Jonny's Towing truck" />
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container cta-panel">
          <div>
            <span className="eyebrow">Need roadside help?</span>
            <h2>Tell us where you are and what happened.</h2>
            <p>Call or text for the fastest response. With permission, Text Now adds your current GPS location to the message.</p>
          </div>
          <div className="hero-actions">
            <a className="button button--primary" href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch"><Phone size={19} /> Call now</a>
            <TextNowLink className="button button--secondary"><MessageCircle size={19} /> Text Now</TextNowLink>
          </div>
        </div>
      </section>
    </>
  );
}
